import React, { useState, useEffect, useCallback, useRef } from "react";
import { AgGridColumn, AgGridReact } from "ag-grid-react";
import "ag-grid-enterprise";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine.css";
import Styles from "./styles.module.scss";
import { productData } from "./data";
import { Banner, ContextualSaveBar, DisplayText, Stack, Pagination, Spinner, Frame, Toast, Loading, Modal, TextContainer, Image, Checkbox, Link, ChoiceList, Button, Popover, Filters, Select, Tooltip, TextField, Badge, Icon, Collapsible, RadioButton } from "@shopify/polaris";
import { SortAscendingMajor, SortDescendingMajor, ChevronRightMinor, ChevronDownMinor } from '@shopify/polaris-icons';
import ManageProducts from '../../components/ManageProducts';
import productsApi from "../../services/productsApi";
import roundingApi from "../../services/roundingApi";
import { transformProducts, numberParser, getPrimaryMarket, handleCellClass, rowGroupHeaderName, numberValueFormatter, checkEditableFlag, convertProductsIntoReqFormat } from "../../helpers/product";
import { getQueryStringParameters } from '../../helpers/string';
import { debounce } from "throttle-debounce";
import { getMarkets } from '../../redux/actions/marketActions';
import { connect } from 'react-redux';
import logo from '../../assets/images/Multi Country Pricing.png';
import roundingDisabled from '../../assets/images/roundingDisabled.png';
import FrozeAdminPanel from "../FrozeAdminPanel";
import CsvUploadJobBanner from "../../components/common/CsvUploadJobBanner";
import CsvDownloadJobBanner from "../../components/common/CsvDownloadJobBanner";
import CatalogPriceUpdateBanner from "../../components/common/CatalogPriceUpdateBanner";

let originalProductData = []
let pageMode = 'NEXT'

const MAX_ALLOWED_VARIANTS = 100;
const MAX_ZERO_PRICE_PRODUCTS_LIST = 10;
const ERROR_TOAST_DURATION = 1500;
const LONG_ERROR_TOAST_DURATION = 5000;
const PRODUCT_STATUS_FILTER_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'active' },
  { label: 'Draft', value: 'draft' },
];
const PRICE_CELL_STYLE = {
  borderColor: 'transparent',
  borderRight: "1px solid #D9DCE0",
};

const CATEGORY_PATH_SEPARATOR = /\s*>\s*/;

const parseCategorySegments = (name) =>
  (name || '').split(CATEGORY_PATH_SEPARATOR).map((segment) => segment.trim()).filter(Boolean);

const getCategoryLeafLabel = (segments) => {
  if (!segments.length) {
    return '';
  }
  if (segments.length === 1) {
    return segments[0];
  }
  return `${segments[segments.length - 2]} - ${segments[segments.length - 1]}`;
};

const getCategoryDisplayLabelFromName = (name) =>
  getCategoryLeafLabel(parseCategorySegments(name));

const buildCategoryTree = (categories) => {
  const root = { children: new Map() };

  categories.forEach((category) => {
    const segments = parseCategorySegments(category.name);
    if (!segments.length) {
      return;
    }

    let node = root;
    const path = [];

    segments.forEach((segment, index) => {
      path.push(segment);
      if (!node.children.has(segment)) {
        node.children.set(segment, {
          segment,
          path: [...path],
          children: new Map(),
          category: null,
        });
      }
      node = node.children.get(segment);
      if (index === segments.length - 1) {
        node.category = category;
      }
    });
  });

  return root;
};

const sortCategoryNodes = (nodes, sortOrder) =>
  [...nodes].sort((a, b) =>
    sortOrder === 'asc'
      ? a.segment.localeCompare(b.segment, undefined, { sensitivity: 'base' })
      : b.segment.localeCompare(a.segment, undefined, { sensitivity: 'base' }),
  );

const collectCategoryGroupPaths = (node, paths = new Set()) => {
  [...node.children.values()].forEach((child) => {
    if (child.children.size > 0) {
      paths.add(child.path.join('/'));
      collectCategoryGroupPaths(child, paths);
    }
  });
  return paths;
};

const CategoryFilterTree = ({
  categories,
  sortOrder,
  selectedCategoryIds,
  onCategoryToggle,
}) => {
  const tree = React.useMemo(() => buildCategoryTree(categories), [categories]);
  const [expandedPaths, setExpandedPaths] = React.useState(() => new Set());

  React.useEffect(() => {
    setExpandedPaths(collectCategoryGroupPaths(tree));
  }, [tree]);

  const toggleExpanded = (pathKey) => {
    setExpandedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(pathKey)) {
        next.delete(pathKey);
      } else {
        next.add(pathKey);
      }
      return next;
    });
  };

  const renderLeafCheckbox = (node, isTopLevel = false) => (
    <div
      key={node.category.id}
      className={isTopLevel ? Styles.categoryTopLevelLeaf : Styles.categoryLeafRow}
    >
      <Checkbox
        label={node.segment}
        checked={selectedCategoryIds.includes(node.category.id)}
        onChange={(checked) => onCategoryToggle(node.category.id, checked)}
      />
    </div>
  );

  const renderChildNode = (child) => {
    const pathKey = child.path.join('/');
    const hasChildren = child.children.size > 0;
    const isLeaf = Boolean(child.category);

    if (!hasChildren && isLeaf) {
      return renderLeafCheckbox(child, child.path.length === 1);
    }

    if (!hasChildren) {
      return null;
    }

    const isOpen = expandedPaths.has(pathKey);
    const nestedChildren = sortCategoryNodes([...child.children.values()], sortOrder);

    return (
      <div key={pathKey} className={Styles.categoryAccordionItem}>
        <button
          type="button"
          className={Styles.categoryAccordionHeader}
          onClick={() => toggleExpanded(pathKey)}
          aria-expanded={isOpen}
        >
          <span className={Styles.categoryAccordionChevron}>
            <Icon
              source={isOpen ? ChevronDownMinor : ChevronRightMinor}
              color="base"
            />
          </span>
          <span className={Styles.categoryAccordionTitle}>{child.segment}</span>
        </button>
        <Collapsible open={isOpen} id={`category-accordion-${pathKey}`}>
          <div className={Styles.categoryAccordionBody}>
            {nestedChildren.map((nested) => {
              const nestedIsLeaf = nested.category && nested.children.size === 0;
              if (nestedIsLeaf) {
                return renderLeafCheckbox(nested);
              }
              return renderChildNode(nested);
            })}
          </div>
        </Collapsible>
      </div>
    );
  };

  const topLevelChildren = sortCategoryNodes([...tree.children.values()], sortOrder);

  return (
    <div className={Styles.categoryTree}>
      {topLevelChildren.map((child) => renderChildNode(child))}
    </div>
  );
};

const ProductsContainer = props => {
  const [gridApi, setGridApi] = useState(null)
  const [gridColumnApi, setGridColumnApi] = useState(null);
  const [rowData, setRowData] = useState([])
  const [links, setLinks] = useState({ next: null, prev: null })
  const [errorMessageForPrice, setErrorMessageForPrice] = useState("");
  const [isLoading, setIsLoading] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [isShowToast, setIsShowToast] = useState(false);
  const [isShowErrorToast, setIsShowErrorToast] = useState(false)
  const [errorToastDuration, setErrorToastDuration] = useState(ERROR_TOAST_DURATION)
  const [unSavedProductIds, setUnSavedProductIds] = useState([])
  const [isConfirmModal, setIsConfirmModal] = useState(false);
  const [isShowSKU, setIsShowSKU] = useState(false);
  const [productStatusFilter, setProductStatusFilter] = useState('all');
  const [isProductStatusFilterLoading, setIsProductStatusFilterLoading] = useState(false);
  const [isShowCompareAtPrice, setIsShowCompareAtPrice] = useState(true);
  const [storeRoundings, setStoreRoundings] = useState([]);
  const [searchApiCalled, setSearchApiCalled] = useState(false);
  const [nextPageApiCalled, setNextPageApiCalled] = useState(false);
  const [openRoundingDisabledModal, setOpenRoundingDisabledModal] = useState(false);
  const [verifyRoundingApiCalled, setVerifyRoundingApiCalled] = useState(false);
  const [verifyRoundingError, setVerifyRoundingError] = useState('');
  const [roundingDisabledToast, setRoundingDisabledToast] = useState(false);
  const [pageLimit, setPageLimit] = useState([25]);
  const [popoverActive, setPopoverActive] = useState(false);
  const [pageLimitApiCalled, setPageLimitApiCalled] = useState(false);
  const [statusApiCalled, setStatusApiCalled] = useState(false);
  const [queryValue, setQueryValue] = useState(null);
  const [status, setStatus] = useState(['all']);
  const [selectedMarketInStatus, setSelectedMarketInStatus] = useState(null);
  const [totalCount, setTotalCount] = useState(null);
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [totalProductCountFromAPI, setTotalProductCountFromAPI] = useState(null);
  const [timer, setTimer] = useState(0);
  const [refreshProductsApiCalled, setRefreshProductsApiCalled] = useState(false)
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false)
  const [openFrozeAdminPanel, setOpenFrozeAdminPanel] = useState(false);
  const [hasInitialDataLoaded, setHasInitialDataLoaded] = useState(false);
  const [isMarketErrorModalOpen, setIsMarketErrorModalOpen] = useState(false);
  const [selectedMarketError, setSelectedMarketError] = useState(null);
  const [showAllMarketErrors, setShowAllMarketErrors] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [toastMessage, setToastMessage] = useState("");
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [allSelected, setAllSelected] = useState(false);
  const [isSelectionPaginationModal, setIsSelectionPaginationModal] = useState(false);
  const [isZeroPrimaryPriceModalOpen, setIsZeroPrimaryPriceModalOpen] = useState(false);
  const [zeroPrimaryPriceProducts, setZeroPrimaryPriceProducts] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [availableCollections, setAvailableCollections] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedCollectionIds, setSelectedCollectionIds] = useState([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(true);
  const [collectionsLoading, setCollectionsLoading] = useState(true);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [tagSearchQuery, setTagSearchQuery] = useState('');
  const [collectionSearchQuery, setCollectionSearchQuery] = useState('');
  const [categorySearchQuery, setCategorySearchQuery] = useState('');
  const [tagSortOrder, setTagSortOrder] = useState('asc');
  const [collectionSortOrder, setCollectionSortOrder] = useState('asc');
  const [categorySortOrder, setCategorySortOrder] = useState('asc');
  const gridApiRef = useRef(null);
  const pendingPaginationRef = useRef(null);
  const pendingRemoveProductIdsRef = useRef(null);

  const clearProductSelection = useCallback(() => {
    setSelectedProductIds([]);
    setAllSelected(false);
    if (gridApiRef.current) {
      gridApiRef.current.deselectAll();
    }
  }, []);

  const handleCatalogStatusCheckResult = useCallback((message, isError) => {
    if (isError) {
      setErrorMessage(message);
      setIsShowErrorToast(true);
    } else {
      setToastMessage(message);
      setIsShowToast(true);
    }
  }, []);

  const handleSelectAllToggle = (api) => {


    const grid = api || gridApiRef.current || gridApi;
    if (!grid) {
      return;
    }

    if (allSelected) {
      grid.deselectAll();
    } else {
      grid.selectAll();
    }

    grid.redrawRows();
    setAllSelected(!allSelected);
  };



  const hasMarketPriceValue = (price) => {
    if (price === "" || price == null) {
      return false;
    }
    const parsedPrice = parseFloat(price);
    return !Number.isNaN(parsedPrice) && parsedPrice !== 0;
  };

  const getProductIdsWithOtherMarketPrices = (productIds) => {
    const otherMarketRegionCodes = new Set(
      props.markets
        .filter((market) => !market.isPrimaryMarket)
        .map((market) => market.regionCode),
    );

    if (!otherMarketRegionCodes.size) {
      return [];
    }

    return productIds.filter((productId) => {
      const product = originalProductData.find((item) => item.id == productId);
      if (!product?.variants?.length) {
        return false;
      }

      return product.variants.some(
        (variant) =>
          otherMarketRegionCodes.has(variant.region_code) &&
          hasMarketPriceValue(variant.price),
      );
    });
  };

  const getProductsWithZeroPrimaryPrice = (productIds) => {
    const primaryMarketRegion = storeMarkets.find((market) => market.isPrimaryMarket);
    if (!primaryMarketRegion) {
      return [];
    }

    const priceKey = `${primaryMarketRegion.regionCode}_price`;

    return productIds.reduce((invalidProducts, productId) => {
      const variants = rowData.filter((row) => row.productId === productId);
      if (!variants.length) {
        return invalidProducts;
      }

      const hasZeroPrimaryPrice = variants.some((variant) => {
        const price = variant[priceKey];
        if (price === "" || price == null) {
          return true;
        }
        const parsedPrice = parseFloat(price);
        return Number.isNaN(parsedPrice) || parsedPrice === 0;
      });

      if (hasZeroPrimaryPrice) {
        invalidProducts.push({
          productId,
          productTitle: variants[0].productTitle,
        });
      }

      return invalidProducts;
    }, []);
  };

  const executeDeleteProductsPrices = (productIdsToProcess) => {
    if (!productIdsToProcess.length) {
      return;
    }

    // const primaryMarket = storeMarkets.filter((market) => market.isPrimaryMarket);
    // if (!primaryMarket.length) {
    //   return;
    // }

    // const eligibleProductIds = getProductIdsWithOtherMarketPrices(productIdsToProcess);
    // if (!eligibleProductIds.length) {
    //   setErrorMessage(
    //     "Selected products do not have prices in any other market. Nothing to remove."
    //   );
    //   setErrorToastDuration(LONG_ERROR_TOAST_DURATION);
    //   setIsShowErrorToast(true);
    //   return;
    // }

    setIsLoading(true);
    productsApi
      .removeNonPrimaryPrices({
        productIds: productIdsToProcess.map((id) => String(id)),
      })
      .then((response) => {
        const data = response?.data ?? response;
        if (data?.success === false) {
          setErrorMessage(
            typeof data.message === 'string' && data.message
              ? data.message
              : 'Failed to remove prices.',
          );
          setErrorToastDuration(LONG_ERROR_TOAST_DURATION);
          setIsShowErrorToast(true);
          return;
        }

        setIsShowToast(true);
        setUnSavedProductIds([]);
        clearProductSelection();
        return syncCurrentProducts();
      })
      .catch((error) => {
        const apiMessage =
          error?.error?.data?.message ??
          error?.error?.data?.data?.message ??
          error?.error?.message;
        if (typeof apiMessage === 'string' && apiMessage) {
          setErrorMessage(apiMessage);
        }
        setErrorToastDuration(LONG_ERROR_TOAST_DURATION);
        setIsShowErrorToast(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const closeZeroPrimaryPriceModal = () => {
    setIsZeroPrimaryPriceModalOpen(false);
    setZeroPrimaryPriceProducts([]);
    pendingRemoveProductIdsRef.current = null;
  };

  const confirmContinueWithoutZeroPriceProducts = () => {
    const invalidProductIds = new Set(
      zeroPrimaryPriceProducts.map((product) => product.productId)
    );
    const productIdsToProcess = (pendingRemoveProductIdsRef.current || []).filter(
      (productId) => !invalidProductIds.has(productId)
    );

    closeZeroPrimaryPriceModal();

    if (!productIdsToProcess.length) {
      setErrorMessage("No products available to remove prices. Please update the primary market price first.");
      setIsShowErrorToast(true);
      return;
    }

    executeDeleteProductsPrices(productIdsToProcess);
  };

  const deleteProductsPrices = () => {
    if (!selectedProductIds.length) {
      return;
    }

    // const invalidProducts = getProductsWithZeroPrimaryPrice(selectedProductIds);
    // if (invalidProducts.length > 0) {
    //   pendingRemoveProductIdsRef.current = [...selectedProductIds];
    //   setZeroPrimaryPriceProducts(invalidProducts);
    //   setIsZeroPrimaryPriceModalOpen(true);
    //   return;
    // }

    executeDeleteProductsPrices(selectedProductIds);
  };


  const autocompleteSearch = (searchParams) => {
    fetchProducts([], pageLimit.toString(), status[0] === 'error' ? true : false, selectedMarketInStatus, { query: searchParams })
  }

  // Get markets with errors
  const getMarketsWithErrors = () => {
    const marketsWithErrors = storeMarkets.filter(market => market.isMarketOK === false);
    // console.log('Checking markets for errors:', {
    //   totalMarkets: storeMarkets.length,
    //   marketsWithErrors: marketsWithErrors.length,
    //   errorMarkets: marketsWithErrors.map(m => ({
    //     name: m.marketName,
    //     regionCode: m.regionCode,
    //     isMarketOK: m.isMarketOK,
    //     msg: m.marketOKMsg
    //   }))
    // });
    // console.log('Checking markets for errors:', {
    //   totalMarkets: storeMarkets.length,
    //   marketsWithErrors: marketsWithErrors.length,
    //   errorMarkets: marketsWithErrors.map(m => ({
    //     name: m.marketName,
    //     regionCode: m.regionCode,
    //     isMarketOK: m.isMarketOK,
    //     msg: m.marketOKMsg
    //   }))
    // });
    return marketsWithErrors;
  };

  const openMarketSolutionModal = (market) => {
    setSelectedMarketError(market);
    setIsMarketErrorModalOpen(true);
  };

  const closeMarketSolutionModal = () => {
    setIsMarketErrorModalOpen(false);
    setSelectedMarketError(null);
  };

  const handleMarkAsResolved = () => {
    closeMarketSolutionModal();
    window.location.href = '/';
  };

  const getScenarioSteps = (marketOKSteps, storeDomain) => {
    const storeLink = `https://admin.shopify.com/store/${storeDomain}/settings/markets`;

    const scenarios = {
      'catalog-reassign': {
        title: 'SCENARIO 1: Catalog Reassignment',
        subtitle: 'When the MCP catalog exists but is not assigned to the correct market',
        steps: [
          `Click on the market link provided above to open your Shopify Markets settings in a new tab`,
          `Locate and click on the specific market name that needs catalog reassignment`,
          `Scroll down to the "Catalogs" in Customized or Inherited section`,
          `Click on the "+" button`,
          `From the dropdown menu, select the MCP linked catalog name (MCPMarketCatalog: "catalogName" that should remain) and remove unnecessary Catalogs`,
          `Click "Save" to apply the changes`,
          `Wait for 2-3 minutes for the system to sync`,
          `Return to the MCP app and refresh the page to verify the connection is restored`
        ]
      },
      'catalog-create': {
        title: 'SCENARIO 2: Catalog Creation/Replacement',
        subtitle: 'When the MCP catalog no longer exists or needs to be created',
        steps: [
          `Click on the market link provided above to open your Shopify Markets settings in a new tab`,
          `Locate and click on the specific market name that needs a new catalog`,
          `Scroll down to the "Catalogs" in the Customized or Inherited section`,
          `Click on the "+" button`,
          `Choose one of the following options:`,
          `Option A: Use an existing catalog - Select an available MCP linked catalog from the dropdown menu and click "Save"`,
          `Option B: Create a new catalog - Click on "Create new catalog", enter a catalog name (use a descriptive name like "MCP - [Market Name]"), add products to the catalog as needed, click "Save", then return to the market settings and assign this newly created catalog`,
          `Contact our Customer Support team immediately after completing the above steps with: Market name, Catalog name you assigned, Your store domain`,
          `Our team will re-establish the backend connection within 2-3 business days`,
          `You will receive a confirmation email once the linking is complete`
        ]
      },
      'remove-extra-catalogs': {
        title: 'SCENARIO 3: Remove Extra Catalogs',
        subtitle: 'When a market has multiple catalogs assigned (should only have one MCP linked catalog)',
        steps: [
          `Click on the market link provided above to open your Shopify Markets settings in a new tab`,
          `Locate and click on the market name`,
          `Scroll down to the "Catalogs" in Customized or Inherited section`,
          `You should see multiple catalogs currently assigned`,
          `Click on the "+" button`,
          `Select only the MCP linked catalog (MCPMarketCatalog: "catalogName" that should remain) and deselect other selected catalogs`,
          `Click "Save"`,
          `Verify that only the MCP linked catalog (MCPMarketCatalog: "catalogName") is currently assigned to market.`,
          `Wait for 2-3 minutes for the system to sync`,
          `Return to the MCP app and refresh the page to verify the connection is restored`
        ]
      }
    };

    return { ...scenarios[marketOKSteps], storeLink };
  };

  const autosizeHeaders = (gridApi) => {
    gridApi.setGroupHeaderHeight(40);
    const headerCells = document.querySelectorAll('.ag-header-group-cell div.ag-react-container');
    let minHeight = 40;
    headerCells.forEach(cell => {
      minHeight = Math.max(minHeight, cell.scrollHeight);
    });
    gridApi.setGroupHeaderHeight(minHeight + 10);
  }

  const onColumnResized = () => {
    setTimeout(() => {
      if (gridApi) {
        autosizeHeaders(gridApi);
      }
    }, 0);
  }

  const GroupRowRenderer = (props) => {
    const firstChild =
      props.node.allLeafChildren?.[0];
    const productName =
      firstChild?.data?.productTitle || "";
    const [hovered, setHovered] =
      React.useState(false);
    const totalVariants =
      props.node.allLeafChildren?.length || 0;
    const expanded = props.node.expanded;
    const [isSelected, setIsSelected] =
      React.useState(
        props.node.isSelected()
      );
    return (
      <div
        className={`
        ${Styles.groupRow}
      `}
      >
        <input
          type="checkbox"
          checked={
            props.node.allLeafChildren?.every(
              child => child.isSelected()
            )
          }
          onChange={(e) => {

            props.node.setSelected(
              e.target.checked
            );
            props.api.redrawRows();

          }}
        />
        <span
          className={
            expanded
              ? "ag-icon ag-icon-tree-open"
              : "ag-icon ag-icon-tree-closed"
          }
          onClick={(e) => {
            e.stopPropagation();

            props.node.setExpanded(
              !expanded
            );
          }}
          style={{
            cursor: "pointer",
            fontSize: "18px",
            color: hovered ? "#1a73e8" : "#666",
            transition: "all 0.2s ease",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "22px",
            height: "22px"
          }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        />
        <span>
          {productName} ({totalVariants})
        </span>
      </div>
    );
  };

  const onSearch = (searchText) => {
    setSearchApiCalled(true);
    setStatus(['all']);
    setErrorMessageForPrice('');
    // if(status[0] === 'error') {
    //   setErrorMessageForPrice('Some products are malfunctioned!')
    // } else {
    //   setErrorMessageForPrice('')
    // }
    // fetchProducts(storeRoundings, pageLimit.toString(), status[0] === 'error' ? true : false, selectedMarketInStatus, { query: searchText })
    fetchProducts(storeRoundings, pageLimit.toString(), false, selectedMarketInStatus, { query: searchText });
  }

  const autocompleteSearchDebounced = debounce(300, autocompleteSearch)

  // Copy the store markets into the local variable
  const marketOptionsForSelect = [];
  let storeMarkets = props.markets;
  for (let market of storeMarkets) {
    marketOptionsForSelect.push({ label: market.regionName, value: market.regionCode });
    if (market.setCurrencyCode) {
      const currencyCode = props.currencies.find(i => i.code === market.setCurrencyCode);
      if (currencyCode) {
        market.setCurrencySymbol = currencyCode.symbol;
      }
    }
  }

  const primaryMarket = storeMarkets.find((market) => market.isPrimaryMarket);
  const hasPrimaryMarket = Boolean(primaryMarket);
  const showPrimaryMarketMissing =
    !props.isMarketLoading && props.markets.length > 0 && !hasPrimaryMarket;

  const getParameterByName = (name, url) => {
    name = name.replace(/[\[\]]/g, '\\$&');
    var regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
      results = regex.exec(url);
    if (!results) return null;
    if (!results[2]) return '';
    return decodeURIComponent(results[2].replace(/\+/g, ' '));
  }

  const buildProductListParams = (extraParams = null, filterOverrides = null) => {
    const params = { ...(extraParams || {}) };
    const tagsList = filterOverrides?.tags ?? selectedTags;
    const collectionsList = filterOverrides?.collections ?? selectedCollectionIds;
    const categoriesList = filterOverrides?.categories ?? selectedCategoryIds;
    if (tagsList.length > 0) {
      params.tag = tagsList.join(',');
    } else {
      delete params.tag;
    }
    if (collectionsList.length > 0) {
      params.collection = collectionsList.join(',');
    } else {
      delete params.collection;
    }
    if (categoriesList.length > 0) {
      params.category = categoriesList.join(',');
    } else {
      delete params.category;
    }
    const publicationStatus =
      filterOverrides?.productStatusFilter ?? productStatusFilter;
    if (publicationStatus && publicationStatus !== 'all') {
      params.status = publicationStatus;
    } else {
      delete params.status;
    }
    delete params.tags;
    return params;
  };

  const applyProductsResponse = (response) => {
    if (response.data._links && response.data._links.prev) {
      const page = getParameterByName('page', response.data._links.prev);
      setCurrentPageNumber(parseInt(page) + 1);
    } else {
      setCurrentPageNumber(1);
    }
    setTotalProductCountFromAPI(response.data.multicurrency_products.length);
    originalProductData = [...response.data.multicurrency_products];
    const transformedProducts = transformProducts(
      response.data.multicurrency_products,
      storeMarkets,
      storeRoundings,
    );
    setRowData(transformedProducts);
    setLinks({
      next: response.data?._links?.next,
      prev: response.data?._links?.prev,
    });
    setHasInitialDataLoaded(true);
  };

  const syncCurrentProducts = () => {
    const priceNotIn =
      status[0] === 'empty' ? selectedMarketInStatus || marketOptionsForSelect[0]?.value : '';
    const pageParams = {
      ...(searchText ? { query: searchText } : {}),
      ...(currentPageNumber > 1 ? { page: String(currentPageNumber) } : {}),
    };
    const requestParams = buildProductListParams(pageParams);

    return productsApi
      .getProducts(
        requestParams,
        pageLimit.toString(),
        status[0] === 'error',
        priceNotIn,
      )
      .then((response) => {
        applyProductsResponse(response);
        setIsShowToast(true);
      });
  };

  const fetchProducts = (storeRoundings, pageLimit, onlyInconsistence, priceNotIn, params = null, filterOverrides = null) => {
    const requestParams = buildProductListParams(params, filterOverrides);
    // setIsLoading(true)
    setUnSavedProductIds([]);
    // Each page has its own selection; clear when loading a new page (next/prev/search/filter).
    clearProductSelection();
    return productsApi.getProductCount(requestParams, onlyInconsistence, priceNotIn).then(count => {
      return productsApi.getProducts(requestParams, pageLimit, onlyInconsistence, priceNotIn).then((response) => {
        applyProductsResponse(response);
        setTotalCount(count.data.count);
        if (count.data.count <= 0) {
          setErrorMessageForPrice('');
        }
        setTimeout(() => {
          if (gridApiRef.current) {
            gridApiRef.current.deselectAll();
          }
        }, 0);
      })
        .catch(err => {
          if (typeof err?.error?.error?.data === "string") {
            setErrorMessage(err.error.error.data);
          }
          setIsShowErrorToast(true);
        });
    })
      .catch(countErr => {
        if (typeof countErr?.error?.error?.data === "string") {
          setErrorMessage(countErr.error.error.data);
        }
        setIsShowErrorToast(true);
      })
      .finally(() => {
        setIsLoading(false);
        setSearchApiCalled(false);
        setNextPageApiCalled(false);
        setPageLimitApiCalled(false);
        setStatusApiCalled(false);
      });
  }
  useEffect(() => {
    if (props.markets.length === 0) {
      props.getMarkets()
    };
  }, [])

  useEffect(() => {
    productsApi
      .getShopProductUniques()
      .then((res) => {
        const body = res?.data ?? res;
        const tags = body?.data?.uniqueTags ?? [];
        const collections = body?.data?.uniqueCollections ?? [];
        const categories = body?.data?.uniqueCategories ?? [];
        setAvailableTags(Array.isArray(tags) ? tags : []);
        setAvailableCollections(
          Array.isArray(collections)
            ? collections.filter((item) => item?.id && item?.name)
            : [],
        );
        setAvailableCategories(
          Array.isArray(categories)
            ? categories.filter((item) => item?.id && item?.name)
            : [],
        );
      })
      .catch(() => {
        setAvailableTags([]);
        setAvailableCollections([]);
        setAvailableCategories([]);
      })
      .finally(() => {
        setTagsLoading(false);
        setCollectionsLoading(false);
        setCategoriesLoading(false);
      });
  }, []);

  // useEffect(() => {
  //   if (props.markets.length > 0) {
  //     setIsLoading(true);
  //     roundingApi.getStoreRoundings().then((response) => {
  //       setStoreRoundings(response.data);
  //       if(response.data && response.data.rounding_rules_enabled !== undefined && !response.data.rounding_rules_enabled) {
  //         setOpenRoundingDisabledModal(true);
  //       }
  //       productsApi.getProductCount(null, true, selectedMarketInStatus).then(count => {
  //         if(count.data && count.data.count && count.data.count > 0) {
  //           // setStatus(['error']);
  //           // setErrorMessageForPrice("Some products are malfunctioned!");
  //           fetchProducts(response.data, pageLimit.toString(), true, selectedMarketInStatus);
  //         } else {
  //           fetchProducts(response.data, pageLimit.toString(), status[0] === 'error' ? true : false, selectedMarketInStatus);
  //         }
  //       });
  //     })
  //     .catch(err => {
  //       setIsShowErrorToast(true);
  //     });
  //   }
  // }, [props.markets])
  // useEffect(() => {
  //   if (props.markets.length > 0) {
  //     setIsLoading(true);
  //     productsApi.getProductCount(null, false, selectedMarketInStatus).then(count => {
  //       if(count.data && count.data.count && count.data.count > 0) {
  //         setStatus(['all']);
  //         // setErrorMessageForPrice("Some products are malfunctioned!");
  //         fetchProducts({}, pageLimit.toString(), false, selectedMarketInStatus);
  //       } else {
  //         fetchProducts({}, pageLimit.toString(), status[0] === 'error' ? true : false, selectedMarketInStatus);
  //       }
  //     })
  //     .catch(err => {
  //       setIsShowErrorToast(true);
  //     });
  //   }
  // }, [props.markets])

  useEffect(() => {
    async function fetchAllData() {
      if (props.markets.length === 0) return;
      try {
        // // Step 1: Get rounding info
        // const roundingResponse = await roundingApi.getStoreRoundings();
        // const storeRoundingsData = roundingResponse.data;
        // setStoreRoundings(storeRoundingsData);

        // // Check if rounding rules are disabled
        // if (
        //   storeRoundingsData &&
        //   storeRoundingsData.rounding_rules_enabled !== undefined &&
        //   !storeRoundingsData.rounding_rules_enabled
        // ) {
        //   setOpenRoundingDisabledModal(true);
        // }

        // // Step 2: Get product count with `includeRounding = true`
        // const countResponse = await productsApi.getProductCount(
        //   null,
        //   true,
        //   selectedMarketInStatus
        // );
        // const productCount = countResponse?.data?.count || 0;

        // if (productCount > 0) {
        //   // Products with price issues exist
        //   // setStatus(['error']);
        //   // setErrorMessageForPrice("Some products are malfunctioned!");
        //   fetchProducts(storeRoundingsData, pageLimit.toString(), true, selectedMarketInStatus);
        // } else {
        //   // If status is already 'error', we keep true for includeRounding
        //   const includeRounding = status[0] === "error";
        //   fetchProducts(storeRoundingsData, pageLimit.toString(), status[0] === 'error' ? true : false, selectedMarketInStatus);
        // }
        if (props.markets.length > 0) {
          if (!props.markets.some((market) => market.isPrimaryMarket)) {
            setIsLoading(false);
            return;
          }
          setIsLoading(true);
          roundingApi.getStoreRoundings().then((response) => {
            setStoreRoundings(response.data);
            if (response.data && response.data.rounding_rules_enabled !== undefined && !response.data.rounding_rules_enabled) {
              setOpenRoundingDisabledModal(true);
            }
            productsApi.getProductCount(null, true, selectedMarketInStatus).then(count => {
              if (count.data && count.data.count && count.data.count > 0) {
                // setStatus(['error']);
                // setErrorMessageForPrice("Some products are malfunctioned!");
                fetchProducts(response.data, pageLimit.toString(), true, selectedMarketInStatus);
              } else {
                fetchProducts(response.data, pageLimit.toString(), status[0] === 'error' ? true : false, selectedMarketInStatus);
              }
            });
          })
            .catch(err => {
              if (typeof err?.message === "string") {
                setErrorMessage(err.message);
              }
              setIsShowErrorToast(true);
            });
        }
      } catch (error) {
        if (typeof error?.error?.error?.data === "string") {
          setErrorMessage(error.error.error.data);
        }
        setIsShowErrorToast(true);
      }
    };

    fetchAllData();
  }, [props.markets]);


  function onGridReady(params) {
    gridApiRef.current = params.api;
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  }

  const handleCheckboxChange = useCallback((newChecked, field) => {
    if (field === "isShowSKU") {
      setIsShowSKU(newChecked)
    } else {
      setIsShowCompareAtPrice(newChecked);
    }
  }, []);

  const handleProductStatusFilterChange = useCallback((value) => {
    if (isProductStatusFilterLoading || value === productStatusFilter) {
      return;
    }
    setProductStatusFilter(value);
    setStatusApiCalled(true);
    setIsProductStatusFilterLoading(true);
    const priceNotIn =
      status[0] === 'empty' ? selectedMarketInStatus || marketOptionsForSelect[0]?.value : '';
    fetchProducts(
      storeRoundings,
      pageLimit.toString(),
      status[0] === 'error',
      priceNotIn,
      searchText ? { query: searchText } : null,
      { productStatusFilter: value },
    ).finally(() => {
      setIsProductStatusFilterLoading(false);
    });
  }, [storeRoundings, pageLimit, status, selectedMarketInStatus, searchText, isProductStatusFilterLoading, productStatusFilter]);
  const checkEditableFlagWithMarketError = (params) => {
    if (!params || !params.colDef) return false;

    const regionCode = params.colDef.field.split('_')[0];

    const market = storeMarkets.find(m => m.regionCode === regionCode);
    const hasMarketError = market && market.isMarketOK === false;

    if (hasMarketError) {
      return false;
    }

    return checkEditableFlag(params);
  };

  const validateCell = params => {
    // console.log(params.value,"params.value")

    const productHavingValue = rowData.filter(
      p => p.productId == params.data.productId && p[params.colDef.field] != ""
    );
    const colId = params.column.getId();
    const regionCode = colId.split('_')[0]
    const toBeUpdatedColId = `${regionCode}_invalid`
    const market = storeMarkets.find(m => m.regionCode === regionCode);
    const hasMarketError = market && market.isMarketOK === false;

    if (hasMarketError) {
      return {
        backgroundColor: "#fef3f2",
        borderColor: "#bf0711",
        borderRight: "1px solid #bf0711",
        color: "#6d0000"
      };
    }

    if (params.value == "" && (productHavingValue.length > 0 || getPrimaryMarket(storeMarkets, regionCode))) {
      //mark police cells as red
      params.node.setDataValue(toBeUpdatedColId, true);
      return { color: "black", borderColor: "red" };
    } else {
      params.node.setDataValue(toBeUpdatedColId, false);
      return PRICE_CELL_STYLE
    }
  };
  const handleDataChanged = params => {
    var colId = params.column.getId();
    if (params.newValue !== undefined && params.oldValue !== null && params.newValue != params.oldValue) {
      // params.node.setDataValue(colId, 45);
      if (params.api) {
        params.api.refreshCells({ rowNodes: params.node.parent.allLeafChildren, force: true })
        // params.api.redrawRows({ rowNodes: params.node.parent.allLeafChildren, force: true });
        // const cell = params.api.getFocusedCell()
        // if (cell) {
        //   params.api.setFocusedCell(cell.rowIndex, cell.column)
        // }
        // gridApi.refreshCells({ rowNodes: params.node.parent.allLeafChildren, columns: [params.column.colId], force: true });
        // Check 'Invalid flag' in row data
        let errorMessage = ""
        for (const row of rowData) {
          for (const storeMarket of storeMarkets) {
            if (row[`${storeMarket.regionCode}_invalid`]) {
              if (getPrimaryMarket(storeMarkets, storeMarket.regionCode)) {
                errorMessage = "Error! Primary market price can't be null.";
                break
              }
              errorMessage = "Warning! All variants of a product must have prices set in a given market.";
              break
            }
            if (errorMessage)
              break
          }
        }
        //Keep the track of updated products
        setUnSavedProductIds([...new Set([...unSavedProductIds, params.data.productId])])

        // Check for maximum 100 variants validation
        const toBeUpdatedRowData = [...rowData]
        const variants = toBeUpdatedRowData.filter((r) => r.productId == params.data.productId)
        const maxAllowedMarkets = Math.floor(MAX_ALLOWED_VARIANTS / variants.length)
        let marketsHavingValue = 0
        for (const market of storeMarkets) {
          // Check any variant for this market have a value
          let variantsHavingVal = variants.filter((v) => v[`${market.regionCode}_price`])

          if (variantsHavingVal.length > 0) {
            marketsHavingValue += 1

          }
        }

        // if (marketsHavingValue >= maxAllowedMarkets) {
        //   for (const market of storeMarkets) {
        //     let variantsNotHavingVal = variants.filter((v) => !v[`${market.regionCode}_price`])
        //     if (variantsNotHavingVal.length === variants.length) {
        //       for (const v of variantsNotHavingVal) {
        //         v[`${market.regionCode}_invalid_for_hundred`] = true;
        //         v[`${market.regionCode}_invalid_for_hundred_tooltip`] = "Exceeded Shopify limit of maximum 100 variants per product.";
        //       }
        //     }
        //     let variantsHavingVal = variants.filter((v) => v[`${market.regionCode}_price`])
        //     for (const v of variantsHavingVal) {
        //       v[`${market.regionCode}_invalid_for_hundred`] = false;
        //       v[`${market.regionCode}_invalid_for_hundred_tooltip`] = "";
        //     }
        //   }
        // } 
        // else {
        //   for (const market of storeMarkets) {
        //     for (const v of variants) {
        //       v[`${market.regionCode}_invalid_for_hundred`] = false;
        //       v[`${market.regionCode}_invalid_for_hundred_tooltip`] = "";
        //     }
        //   }
        // }
        for (const market of storeMarkets) {
          for (const v of variants) {
            v[`${market.regionCode}_invalid_for_hundred`] = false;
            v[`${market.regionCode}_invalid_for_hundred_tooltip`] = "";
          }
        }
        // console.log("inside", status)
        // if(status === "error") {
        //   errorMessage = "Some products are malfunctioned!";
        // }
        setRowData(toBeUpdatedRowData)
        // params.api.redrawRows({ rowNodes: params.node.parent.allLeafChildren, force: true });
        // const cell = params.api.getFocusedCell()
        // if (cell) {
        //   params.api.setFocusedCell(cell.rowIndex, cell.column)
        // }
        if (errorMessage) {
          setErrorMessageForPrice(errorMessage)
          return
        }
        setErrorMessageForPrice('');
      }
    }

  };
  const MyHeaderGroupComponent = params => {
    const values = params.displayName.split("_");
    let roundingRule = {};
    if (values[1] && storeRoundings && storeRoundings.rounding_rules) {
      roundingRule = storeRoundings.rounding_rules.find(i => i.currency === values[1]);
    }
    let isPrimaryMarket = false;
    let isROWAvailable = false;
    const primaryMarket = storeMarkets.find(i => i.isPrimaryMarket);
    if (primaryMarket && primaryMarket.regionName === values[0]) {
      isPrimaryMarket = true;
      for (let m of storeMarkets) {
        if (m.regionCode === "intl") {
          isROWAvailable = true;
          break;
        }
      }
    }

    const showFixButton = params.showFixButton || false;
    const marketData = params.marketData;
    const hasMarketError = params.hasMarketError || false;

    // const btnClickedHandler = () => {
    //   params.clicked(params);
    // };

    const handleFixClick = (e) => {
      e.stopPropagation(); // Prevent event bubbling
      if (params.onFixClick && marketData) {
        params.onFixClick(marketData);
      }
    };

    const headerStyle = hasMarketError ? {
      backgroundColor: '#fef3f2',
      borderLeft: '4px solid #bf0711',
      padding: '16px 28px'
    } : {};

    return (
      <>
        <div className="productHeader" style={headerStyle}>
          <div>
            <span style={hasMarketError ? { color: '#bf0711', fontWeight: '600' } : {}}>
              {values[0]}{isPrimaryMarket && !isROWAvailable ? " + International" : ""}
            </span>{' '}
            {values[1] && <span style={hasMarketError ? { color: '#bf0711', fontWeight: '600' } : {}}>({values[1]})</span>}
            {/* {showFixButton && (
            <Button
              size="slim"
              onClick={handleFixClick}
              style={{ marginLeft: '8px' }}
              destructive
            >
              Fix
            </Button>
          )} */}

            {/* <span className="priceHeader">
            <Tooltip content="This is tooltip for pricing header">
              <Icon source={CircleInformationMajor} color="base" />
            </Tooltip>
          </span> */}
          </div>
          {values[1] && roundingRule && <div className="roundedTo">Rounded to {roundingRule.rule === 1 ? roundingRule.rule.toFixed(2) : roundingRule.rule}</div>}
          {/* <span>
          {values[0]} {values[1] && <span>({(values[1])}{''}
          {roundingRule ? ", rounding to " + roundingRule.rule : ""}</span>})
          <span className="priceHeader">
            <Tooltip content="This is tooltip for pricing header">
              <Icon source={CircleInformationMajor} color="base" />
            </Tooltip>
          </span>
        </span> */}
          {/* <button onClick={btnClickedHandler}>Edit</button> */}
        </div>
      </>
    );
  };

  const productTitleComponent = params => {
    return (
      <div className={Styles["productTitleComponent"]}>
        {params.data.productImage && <span className="variantImg"><img src={params.data.productImage} /></span>}
        <span>{params.value}</span>
      </div>
    );
  }

  const handleHeadClick = field => {
    alert(`${field.displayName} was clicked`);
  };

  const handleFixMarket = (marketData) => {
    openMarketSolutionModal(marketData);
  };

  const updateSearchText = useCallback(
    (value) => {
      setSearchText(value);
      // autocompleteSearchDebounced(value)
    },
    [isLoading],
  );
  const loadProductsPage = (pageObj) => {
    setNextPageApiCalled(true);
    if (status[0] === 'error') {
      setErrorMessageForPrice('Some products are malfunctioned!');
    } else {
      setErrorMessageForPrice('');
    }
    fetchProducts(
      storeRoundings,
      pageLimit.toString(),
      status[0] === 'error',
      selectedMarketInStatus,
      pageObj
    );
  };

  const closeSelectionPaginationModal = () => {
    setIsSelectionPaginationModal(false);
    pendingPaginationRef.current = null;
  };

  const confirmSelectionPagination = () => {
    const direction = pendingPaginationRef.current;
    closeSelectionPaginationModal();
    clearProductSelection();
    const pageObj =
      direction === 'NEXT'
        ? getQueryStringParameters(links.next)
        : getQueryStringParameters(links.prev);
    loadProductsPage(pageObj);
  };

  /**
   * Call the Api to get the previous set of products
   */
  const getPreviousProducts = () => {
    if (unSavedProductIds.length > 0) {
      setIsConfirmModal(true);
      pageMode = 'PREV';
      return;
    }
    if (selectedProductIds.length > 0) {
      pendingPaginationRef.current = 'PREV';
      setIsSelectionPaginationModal(true);
      return;
    }
    loadProductsPage(getQueryStringParameters(links.prev));
  };

  /**
   * Call the Api to get the next set of products
   */
  const getNextProducts = () => {
    if (unSavedProductIds.length > 0) {
      setIsConfirmModal(true);
      pageMode = 'NEXT';
      return;
    }
    if (selectedProductIds.length > 0) {
      pendingPaginationRef.current = 'NEXT';
      setIsSelectionPaginationModal(true);
      return;
    }
    loadProductsPage(getQueryStringParameters(links.next));
  };
  /**
   * Do the transformation of the data as api expects
   */
  const saveProducts = () => {
    const putData = { multicurrency_products: convertProductsIntoReqFormat(unSavedProductIds, rowData, storeMarkets) }
    setIsLoading(true)
    productsApi.updateProducts(putData).then((response) => {
      setIsShowToast(true)
      setUnSavedProductIds([]);
      for (let data of putData.multicurrency_products) {
        const originalData = originalProductData.find(i => i.id === data.id);

        // console.log('originalDataoriginalData', originalData);
        if (originalData) {
          originalData.variants = data.variants;
        }
      }
      const transformedProducts = transformProducts(originalProductData, storeMarkets, storeRoundings);
      // console.log('transformedProductstransformedProducts', transformedProducts);
      setRowData(transformedProducts);
    }).catch((error) => {
      if (typeof error?.error?.data?.message === "string") {
        setErrorMessage(error.error.data.message);
      }
      setErrorToastDuration(LONG_ERROR_TOAST_DURATION);
      setIsShowErrorToast(true)
    }).finally(() => {
      clearProductSelection();
      setIsLoading(false)

    })
  }
  /**
   * Reset all the changes
   */
  const discardChanges = () => {
    setIsLoading(true)
    if (status[0] === 'error') {
      setErrorMessageForPrice('Some products are malfunctioned!')
    } else {
      setErrorMessageForPrice('')
    }

    const transformedProducts = transformProducts(originalProductData, storeMarkets, storeRoundings)
    setUnSavedProductIds([])
    // setRowData([])
    setTimeout(() => {
      setRowData([...transformedProducts])
      setIsLoading(false)
    }, 200);
  }
  const leavePage = () => {
    setIsConfirmModal(false)
    let pageObj = getQueryStringParameters(links.next);
    if (pageMode === 'NEXT') {
      pageObj = getQueryStringParameters(links.next);
    } else {
      pageObj = getQueryStringParameters(links.prev);
    }
    setNextPageApiCalled(true);
    if (status[0] === 'error') {
      setErrorMessageForPrice('Some products are malfunctioned!')
    } else {
      setErrorMessageForPrice('')
    }
    fetchProducts(storeRoundings, pageLimit.toString(), status[0] === 'error' ? true : false, selectedMarketInStatus, pageObj)
  }
  const closeModal = () => {
    setIsConfirmModal(false)
  }

  const getRowStyle = (params) => {
    if (params.data && params.data.inconsistentProduct) {
      return { 'background-color': ' #fdf3f0', color: "red" }
    }
  }

  const keyPressEvent = (event) => {
    const enterKeyPressed = event.keyCode === 13;
    if (enterKeyPressed) {
      onSearch(searchText);
    }
  }

  {/* // const toggleRoundingToast = useCallback(() => setRoundingDisabledToast((toast) => !toast), []);

  // const roundingDisabledToastMarkup = roundingDisabledToast ? (
  //   <Toast content="Rounding is disabled." onDismiss={toggleRoundingToast} />
  // ) : null; */}

  const roundingVerify = () => {
    setVerifyRoundingApiCalled(true);
    roundingApi.verifyRounding().then(verfiyData => {
      if (verfiyData.data.rounding_rule_status) {
        window.location.reload();
      } else {
        setRoundingDisabledToast(true);
        setVerifyRoundingApiCalled(false);
      }
    })
      .catch(verfiyErr => {
        setVerifyRoundingApiCalled(false);
        if (typeof verfiyErr?.error?.error?.data === "string") {
          setErrorMessage(verfiyErr.error.error.data);
        }
        setVerifyRoundingError(true);
      })
  }

  const handlePageLimitChange = (value) => {
    setPageLimitApiCalled(true);
    setPageLimit(value);
    fetchProducts(storeRoundings, value.toString(), status[0] === 'error' ? true : false, selectedMarketInStatus)
  }

  const handleStatusChange = (value) => {
    setStatusApiCalled(true);
    if (value[0] !== "empty") {
      setSelectedMarketInStatus(null);
    } else {
      setSelectedMarketInStatus(marketOptionsForSelect[0].value);
    }
    setStatus(value);
    if (value[0] === 'error') {
      setErrorMessageForPrice('Some products are malfunctioned!')
    } else {
      setErrorMessageForPrice('')
    }
    fetchProducts(storeRoundings, pageLimit.toString(), value[0] === 'error' ? true : false, value[0] === 'empty' ? marketOptionsForSelect[0].value : '')
  }

  const handleFiltersQueryChange = useCallback((value) => {
    setQueryValue(value);
  }, []);

  const togglePopoverActive = useCallback(
    () => setPopoverActive((popoverActive) => !popoverActive),
    [],
  );

  const activator = (
    <Button onClick={togglePopoverActive} disclosure>
      {`${pageLimit.toString()} Products/Page`}
    </Button>
  );

  const getCollectionLabel = (collectionId) => {
    const collection = availableCollections.find((item) => item.id === collectionId);
    return collection?.name ?? collectionId;
  };

  const getCategoryLabel = (categoryId) => {
    const category = availableCategories.find((item) => item.id === categoryId);
    return category ? getCategoryDisplayLabelFromName(category.name) : categoryId;
  };

  const disambiguateLabel = (key, value) => {
    switch (key) {
      case 'productsPerPage':
        return `Products per page is ${pageLimit}`;
      case 'status':
        return `Show is ${status[0] === 'empty' ? 'Products whose price is not set in the market' : (status[0] === 'all' ? 'All products' : 'Products having issues')}`;
      case 'tags':
        return `Tags: ${selectedTags.join(', ')}`;
      case 'collections':
        return `Collections: ${selectedCollectionIds.map(getCollectionLabel).join(', ')}`;
      case 'categories':
        return `Categories: ${selectedCategoryIds.map(getCategoryLabel).join(', ')}`;
      default:
        return value;
    }
  }

  const isEmpty = (value) => {
    if (Array.isArray(value)) {
      return value.length === 0;
    } else {
      return value === '' || value == null;
    }
  }

  const handleProductsPerPageRemove = useCallback(() => setPageLimit(null), []);

  const handleStatusRemove = useCallback(() => setStatus(null), []);

  const handleSelectChange = (val) => {
    setStatusApiCalled(true);
    setSelectedMarketInStatus(val);
    if (status[0] === 'error') {
      setErrorMessageForPrice('Some products are malfunctioned!')
    } else {
      setErrorMessageForPrice('')
    }
    fetchProducts(storeRoundings, pageLimit.toString(), status[0] === 'error' ? true : false, val)
  };

  const handleTagToggle = (tag, checked) => {
    setStatusApiCalled(true);
    const nextTags = checked
      ? [...selectedTags, tag]
      : selectedTags.filter((t) => t !== tag);
    setSelectedTags(nextTags);
    const priceNotIn =
      status[0] === 'empty' ? selectedMarketInStatus || marketOptionsForSelect[0]?.value : '';
    fetchProducts(
      storeRoundings,
      pageLimit.toString(),
      status[0] === 'error',
      priceNotIn,
      searchText ? { query: searchText } : null,
      { tags: nextTags },
    );
  };

  const handleTagsRemove = useCallback(() => {
    setSelectedTags([]);
    setStatusApiCalled(true);
    const priceNotIn =
      status[0] === 'empty' ? selectedMarketInStatus || marketOptionsForSelect[0]?.value : '';
    fetchProducts(
      storeRoundings,
      pageLimit.toString(),
      status[0] === 'error',
      priceNotIn,
      searchText ? { query: searchText } : null,
      { tags: [] },
    );
  }, [storeRoundings, pageLimit, status, selectedMarketInStatus, searchText]);

  const handleCollectionToggle = (collectionId, checked) => {
    setStatusApiCalled(true);
    const nextCollectionIds = checked
      ? [...selectedCollectionIds, collectionId]
      : selectedCollectionIds.filter((id) => id !== collectionId);
    setSelectedCollectionIds(nextCollectionIds);
    const priceNotIn =
      status[0] === 'empty' ? selectedMarketInStatus || marketOptionsForSelect[0]?.value : '';
    fetchProducts(
      storeRoundings,
      pageLimit.toString(),
      status[0] === 'error',
      priceNotIn,
      searchText ? { query: searchText } : null,
      { collections: nextCollectionIds },
    );
  };

  const handleCollectionsRemove = useCallback(() => {
    setSelectedCollectionIds([]);
    setStatusApiCalled(true);
    const priceNotIn =
      status[0] === 'empty' ? selectedMarketInStatus || marketOptionsForSelect[0]?.value : '';
    fetchProducts(
      storeRoundings,
      pageLimit.toString(),
      status[0] === 'error',
      priceNotIn,
      searchText ? { query: searchText } : null,
      { collections: [] },
    );
  }, [storeRoundings, pageLimit, status, selectedMarketInStatus, searchText]);

  const handleCategoryToggle = (categoryId, checked) => {
    setStatusApiCalled(true);
    const nextCategoryIds = checked
      ? [...selectedCategoryIds, categoryId]
      : selectedCategoryIds.filter((id) => id !== categoryId);
    setSelectedCategoryIds(nextCategoryIds);
    const priceNotIn =
      status[0] === 'empty' ? selectedMarketInStatus || marketOptionsForSelect[0]?.value : '';
    fetchProducts(
      storeRoundings,
      pageLimit.toString(),
      status[0] === 'error',
      priceNotIn,
      searchText ? { query: searchText } : null,
      { categories: nextCategoryIds },
    );
  };

  const handleCategoriesRemove = useCallback(() => {
    setSelectedCategoryIds([]);
    setStatusApiCalled(true);
    const priceNotIn =
      status[0] === 'empty' ? selectedMarketInStatus || marketOptionsForSelect[0]?.value : '';
    fetchProducts(
      storeRoundings,
      pageLimit.toString(),
      status[0] === 'error',
      priceNotIn,
      searchText ? { query: searchText } : null,
      { categories: [] },
    );
  }, [storeRoundings, pageLimit, status, selectedMarketInStatus, searchText]);

  const openConfirmationModal = () => {
    productsApi.getProductCount().then(countData => {
      const count = countData.data.count || 0;
      setTimer(count * 2 * 1000);
      setIsConfirmationModalOpen(true);
    }).catch(error => {
      if (error.error !== 'Invalid Session') {
        if (typeof error?.error?.error?.data === "string") {
          setErrorMessage(error.error.error.data);
        }
        setIsShowErrorToast(true);
      }
    })
  }
  const closeConfirmationModal = () => { setIsConfirmationModalOpen(false) }

  const handleRefreshProducts = async () => {
    setRefreshProductsApiCalled(true);
    await productsApi.refreshProducts(props.storeId)
    setIsShowToast(true)
    setRefreshProductsApiCalled(false);
    closeConfirmationModal();
    if (timer > 2000) {
      setOpenFrozeAdminPanel(true);
    }
  }

  const closeFreezeAdminPanel = () => {
    setOpenFrozeAdminPanel(false);
    window.location.reload();
  }
  const onClearAll = () => {
    setStatusApiCalled(true);
    setPageLimit([25]);
    setStatus(['all']);
    setSelectedTags([]);
    setSelectedCollectionIds([]);
    setSelectedCategoryIds([]);
    setErrorMessageForPrice('');
    fetchProducts(storeRoundings, '25', false, null, null, { tags: [], collections: [], categories: [] })
  }

  const getSaveButtonState = errorMessageForPrice ? true : false
  if (rowData.length <= 0 && isLoading && !searchText) {
    return (<div className="products-page-loading" style={{ height: '100px' }}>
      <Frame>
        <Loading />
      </Frame>
    </div>)
  }

  // if (rowData.length <= 0 && !searchText)
  //  return null

  const filters = [
    {
      key: 'productsPerPage',
      label: 'Product/Page',
      filter: (
        <ChoiceList
          title={`Product/Page`}
          titleHidden
          choices={[
            { label: '25', value: 25 },
            { label: '50', value: 50 },
            { label: '100', value: 100 },
          ]}
          selected={pageLimit || [25]}
          onChange={(e) => { handlePageLimitChange(e) }}
        />
      ),
    },
    {
      key: 'status',
      label: 'Show',
      filter: (
        <>
          <ChoiceList
            title="Show"
            titleHidden
            choices={[
              { label: 'All products', value: 'all' },
              // {label: 'Products having issues', value: 'error'},
              { label: 'Products whose price is not set in the market', value: 'empty' },
            ]}
            selected={status || []}
            onChange={(e) => { handleStatusChange(e) }}
          />
          {status[0] === 'empty' && <div className="mt-10">
            <Select
              // label="Date range"
              options={marketOptionsForSelect}
              onChange={(e) => { handleSelectChange(e) }}
              value={selectedMarketInStatus}
            />
          </div>}
        </>
      ),
    },
    {
      key: 'tags',
      label: 'Tags',
      filter: (
        <div className={`${Styles.productTagsFilter} product-tags-filter`}>
          {selectedTags.length > 0 && (
            <div className="Polaris-Filters__AppliedFilterBadgeContainer">
              <Badge status="new" size="small">
                {`Tags: ${selectedTags.join(', ')}`}
              </Badge>
            </div>
          )}
          {!tagsLoading && availableTags.length > 0 && (
            <div className={Styles.productTagsFilterSearchRow}>
              <div className={Styles.productTagsFilterSearch}>
                <TextField
                  label="Search tags"
                  labelHidden
                  placeholder="Search tags"
                  value={tagSearchQuery}
                  onChange={setTagSearchQuery}
                  clearButton
                  onClearButtonClick={() => setTagSearchQuery('')}
                />
              </div>
              <div className={Styles.productTagsFilterSort}>
                <button
                  type="button"
                  className={Styles.productTagsSortButton}
                  onClick={() => setTagSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
                  aria-label={
                    tagSortOrder === 'asc'
                      ? 'Sort tags descending'
                      : 'Sort tags ascending'
                  }
                >
                  <Icon
                    source={tagSortOrder === 'asc' ? SortAscendingMajor : SortDescendingMajor}
                    color="highlight"
                  />
                </button>
              </div>
            </div>
          )}
          <div className={Styles.productTagsFilterList}>
            {tagsLoading ? (
              <Spinner accessibilityLabel="Loading tags" size="small" color="teal" />
            ) : availableTags.length === 0 ? (
              <span>No tags found</span>
            ) : (() => {
              const query = tagSearchQuery.trim().toLowerCase();
              const filteredTags = (query
                ? availableTags.filter((tag) => tag.toLowerCase().includes(query))
                : [...availableTags]
              ).sort((a, b) =>
                tagSortOrder === 'asc'
                  ? a.localeCompare(b, undefined, { sensitivity: 'base' })
                  : b.localeCompare(a, undefined, { sensitivity: 'base' }),
              );
              if (filteredTags.length === 0) {
                return <span>No matching tags</span>;
              }
              return (
                <Stack vertical spacing="tight">
                  {filteredTags.map((tag) => (
                    <Checkbox
                      key={tag}
                      label={tag}
                      checked={selectedTags.includes(tag)}
                      onChange={(checked) => handleTagToggle(tag, checked)}
                    />
                  ))}
                </Stack>
              );
            })()}
          </div>
        </div>
      ),
    },
    {
      key: 'collections',
      label: 'Collections',
      filter: (
        <div className={`${Styles.productTagsFilter} product-collections-filter`}>
          {selectedCollectionIds.length > 0 && (
            <div className="Polaris-Filters__AppliedFilterBadgeContainer">
              <Badge status="new" size="small">
                {`Collections: ${selectedCollectionIds.map(getCollectionLabel).join(', ')}`}
              </Badge>
            </div>
          )}
          {!collectionsLoading && availableCollections.length > 0 && (
            <div className={Styles.productTagsFilterSearchRow}>
              <div className={Styles.productTagsFilterSearch}>
                <TextField
                  label="Search collections"
                  labelHidden
                  placeholder="Search collections"
                  value={collectionSearchQuery}
                  onChange={setCollectionSearchQuery}
                  clearButton
                  onClearButtonClick={() => setCollectionSearchQuery('')}
                />
              </div>
              <div className={Styles.productTagsFilterSort}>
                <button
                  type="button"
                  className={Styles.productTagsSortButton}
                  onClick={() => setCollectionSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
                  aria-label={
                    collectionSortOrder === 'asc'
                      ? 'Sort collections descending'
                      : 'Sort collections ascending'
                  }
                >
                  <Icon
                    source={collectionSortOrder === 'asc' ? SortAscendingMajor : SortDescendingMajor}
                    color="highlight"
                  />
                </button>
              </div>
            </div>
          )}
          <div className={Styles.productTagsFilterList}>
            {collectionsLoading ? (
              <Spinner accessibilityLabel="Loading collections" size="small" color="teal" />
            ) : availableCollections.length === 0 ? (
              <span>No collections found</span>
            ) : (() => {
              const query = collectionSearchQuery.trim().toLowerCase();
              const filteredCollections = (query
                ? availableCollections.filter((collection) =>
                  collection.name.toLowerCase().startsWith(query),
                )
                : [...availableCollections]
              ).sort((a, b) =>
                collectionSortOrder === 'asc'
                  ? a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
                  : b.name.localeCompare(a.name, undefined, { sensitivity: 'base' }),
              );
              if (filteredCollections.length === 0) {
                return <span>No matching collections</span>;
              }
              return (
                <Stack vertical spacing="tight">
                  {filteredCollections.map((collection) => (
                    <Checkbox
                      key={collection.id}
                      label={collection.name}
                      checked={selectedCollectionIds.includes(collection.id)}
                      onChange={(checked) => handleCollectionToggle(collection.id, checked)}
                    />
                  ))}
                </Stack>
              );
            })()}
          </div>
        </div>
      ),
    },
    {
      key: 'categories',
      label: 'Categories',
      filter: (
        <div className={`${Styles.productTagsFilter} product-categories-filter`}>
          {selectedCategoryIds.length > 0 && (
            <div className="Polaris-Filters__AppliedFilterBadgeContainer">
              <Badge status="new" size="small">
                {`Categories: ${selectedCategoryIds.map(getCategoryLabel).join(', ')}`}
              </Badge>
            </div>
          )}
          {!categoriesLoading && availableCategories.length > 0 && (
            <div className={Styles.productTagsFilterSearchRow}>
              <div className={Styles.productTagsFilterSearch}>
                <TextField
                  label="Search categories"
                  labelHidden
                  placeholder="Search by category"
                  value={categorySearchQuery}
                  onChange={setCategorySearchQuery}
                  clearButton
                  onClearButtonClick={() => setCategorySearchQuery('')}
                />
              </div>
              <div className={Styles.productTagsFilterSort}>
                <button
                  type="button"
                  className={Styles.productTagsSortButton}
                  onClick={() => setCategorySortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
                  aria-label={
                    categorySortOrder === 'asc'
                      ? 'Sort categories descending'
                      : 'Sort categories ascending'
                  }
                >
                  <Icon
                    source={categorySortOrder === 'asc' ? SortAscendingMajor : SortDescendingMajor}
                    color="highlight"
                  />
                </button>
              </div>
            </div>
          )}
          <div className={Styles.productTagsFilterList}>
            {categoriesLoading ? (
              <Spinner accessibilityLabel="Loading categories" size="small" color="teal" />
            ) : availableCategories.length === 0 ? (
              <span>No categories found</span>
            ) : (() => {
              const query = categorySearchQuery.trim().toLowerCase();
              const filteredCategories = (query
                ? availableCategories.filter((category) =>
                  category.name.toLowerCase().includes(query),
                )
                : [...availableCategories]
              );
              if (filteredCategories.length === 0) {
                return <span>No matching categories</span>;
              }
              return (
                <CategoryFilterTree
                  categories={filteredCategories}
                  sortOrder={categorySortOrder}
                  selectedCategoryIds={selectedCategoryIds}
                  onCategoryToggle={handleCategoryToggle}
                />
              );
            })()}
          </div>
        </div>
      ),
    },
  ];



  const appliedFilters = [];
  if (!isEmpty(pageLimit)) {
    const key = 'productsPerPage';
    appliedFilters.push({
      key,
      label: disambiguateLabel(key, pageLimit),
      onRemove: handleProductsPerPageRemove,
    });
  }
  if (!isEmpty(status)) {
    const key = 'status';
    appliedFilters.push({
      key,
      label: disambiguateLabel(key, status),
      onRemove: handleStatusRemove,
    });
  }
  if (!isEmpty(selectedTags)) {
    const key = 'tags';
    appliedFilters.push({
      key,
      label: disambiguateLabel(key, selectedTags),
      onRemove: handleTagsRemove,
    });
  }
  if (!isEmpty(selectedCollectionIds)) {
    const key = 'collections';
    appliedFilters.push({
      key,
      label: disambiguateLabel(key, selectedCollectionIds),
      onRemove: handleCollectionsRemove,
    });
  }
  if (!isEmpty(selectedCategoryIds)) {
    const key = 'categories';
    appliedFilters.push({
      key,
      label: disambiguateLabel(key, selectedCategoryIds),
      onRemove: handleCategoriesRemove,
    });
  }

  const SelectAllHeader = (props) => {
    const isAllSelected = props.allSelected ?? false;
    return (
      <div className={Styles.selectAllHeader}>
        <input
          type="checkbox"
          checked={isAllSelected}
          onChange={() => handleSelectAllToggle(props.api)}
          aria-label={isAllSelected ? "Deselect all products" : "Select all products"}
        />
        <span>Products</span>
      </div>
    );
  };

  const visibleZeroPrimaryPriceProducts = zeroPrimaryPriceProducts.slice(
    0,
    MAX_ZERO_PRICE_PRODUCTS_LIST
  );
  const hiddenZeroPrimaryPriceCount = Math.max(
    zeroPrimaryPriceProducts.length - MAX_ZERO_PRICE_PRODUCTS_LIST,
    0
  );
  const invalidZeroPriceProductIds = new Set(
    zeroPrimaryPriceProducts.map((product) => product.productId)
  );
  const canContinueRemovePrices = (pendingRemoveProductIdsRef.current || []).some(
    (productId) => !invalidZeroPriceProductIds.has(productId)
  );

  if (props.isMarketLoading) {
    return (
      <div className={`products-page-loading ${Styles.primaryMarketMissing}`}>
        <Spinner accessibilityLabel="Loading markets" size="large" color="teal" />
      </div>
    );
  }

  if (showPrimaryMarketMissing) {
    return (
      <div className={Styles.primaryMarketMissing}>
        <Banner status="critical" title="Primary market not found">
          <p>
            We could not find a primary market for your store. Product prices cannot be
            displayed until this is resolved. Please contact{" "}
            <a
              rel="noreferrer noopener"
              href="mailto:support@mlveda.com"
              target="_blank"
            >
              <span className="emailLink">support@mlveda.com</span>
            </a>{" "}
            for assistance.
          </p>
        </Banner>
        <div className="mt-15">
          <Button primary onClick={() => props.history.push("/app/markets")}>
            Go to Markets
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <CsvUploadJobBanner isCsvUploadJobRunning={props.isCsvUploadJobRunning} />
      <CsvDownloadJobBanner />
      <CatalogPriceUpdateBanner onStatusCheckResult={handleCatalogStatusCheckResult} />
      {/* {unSavedProductIds.length > 0 && <ContextSaveBar
        disabled={getSaveButtonState}
        onSave={saveProducts}
        onDiscard={discardChanges}
        isLoading={isLoading} />
      } */}
      {unSavedProductIds.length > 0 && <ContextualSaveBar
        contextControl={
          <Stack alignment='center' spacing='tight' distribution='leading'>
            <Stack.Item>
              <Image
                alt=''
                source={logo}
                className='app__contexualSaveBar__logo'
              />
            </Stack.Item>
            <Stack.Item>
              <span className='app__contexualSaveBar__titleText'>
                MLV Multi Country Pricing
              </span>
            </Stack.Item>
          </Stack>
        }
        message="Unsaved changes"
        saveAction={{
          onAction: () => saveProducts(),
          loading: isLoading,
          disabled: getSaveButtonState,
        }}
        discardAction={{
          onAction: () => discardChanges(),
        }}
      />}
      {!isLoading && <div className={Styles["mlv-top-row"]}>
        <ManageProducts
          searchText={searchText}
          updateSearchText={updateSearchText}
          onSearch={onSearch}
          searchApiCalled={searchApiCalled}
          keyPressEvent={keyPressEvent} />


        <div className="more-filters mlv-flex">
          <Filters
            queryValue={queryValue}
            filters={filters}
            appliedFilters={appliedFilters}
            onQueryChange={handleFiltersQueryChange}
            onClearAll={() => { onClearAll() }}
          />
          {/* </div>
          {/* Needs to change styles 
          <div className="more-filters mlv-flex"> */}
          <Button onClick={() => { openConfirmationModal() }} >Sync Products</Button>
          {selectedProductIds.length > 0 ? (
            <Button
              destructive
              onClick={deleteProductsPrices}
            >
              Remove Prices ({selectedProductIds.length})
            </Button>
          ) : (
            <Tooltip content="Please select a product" dismissOnMouseOut>
              <span className={Styles.removePricesButtonWrap}>
                <Button destructive disabled onClick={deleteProductsPrices}>
                  Remove Prices (0)
                </Button>
              </span>
            </Tooltip>
          )}
        </div>




        {/* <div className={`${Styles["fileter-main"]} product-filter`}>
        <Popover
          active={popoverActive}
          activator={activator}
          onClose={(e) => { togglePopoverActive(e) }}
        >
          <ChoiceList
            title={`Product/Page`}
            titleHidden
            choices={[
              {label: '25', value: 25},
              {label: '50', value: 50},
              {label: '100', value: 100},
            ]}
            selected={pageLimit || [25]}
            onChange={(e) => { handlePageLimitChange(e) }}
          />
        </Popover>
          
        </div> */}


        <div className={"justify-content-center " + Styles["pagination-section"]} style={{ pointerEvents: (isLoading || searchApiCalled || nextPageApiCalled || pageLimitApiCalled || statusApiCalled) ? "none" : "auto" }} >
          <span className={Styles["pageNum"]}>
            {totalProductCountFromAPI <= 0 ? 0 : (currentPageNumber * pageLimit) - pageLimit + 1}-{totalProductCountFromAPI <= 0 ? 0 : (currentPageNumber * pageLimit) - pageLimit + totalProductCountFromAPI} of {totalCount}
          </span>
          <Pagination
            hasPrevious={links.prev}
            onPrevious={getPreviousProducts}
            hasNext={links.next}
            onNext={getNextProducts}
            previousKeys={[74]}
            previousTooltip="Previous"
            nextKeys={[75]}
            nextTooltip="Next"
          />
        </div>

      </div>}

      {!isLoading && <div className={Styles["mlv-top-row"]}>
        <div className={Styles["editing-fields"]}>
          {/* <div className={Styles["label"]}>Fields:</div> */}
          <div className={Styles["fields"]}>
            <div className={Styles["product-status-filter"]}>
              <span className={Styles["product-status-label"]}>Status:</span>
              {PRODUCT_STATUS_FILTER_OPTIONS.map(({ label, value }) => (
                <div key={value} className={Styles["choise-list"]}>
                  <RadioButton
                    label={label}
                    checked={productStatusFilter === value}
                    disabled={isProductStatusFilterLoading}
                    id={`product-status-${value}`}
                    name="productStatus"
                    onChange={() => handleProductStatusFilterChange(value)}
                  />
                </div>
              ))}
            </div>
            <div className={Styles["field-checkboxes"]}>
              <span className={Styles["product-fields-label"]}>Fields:</span>
              <div className={Styles["choise-list"]}>
                <Checkbox
                  checked={isShowCompareAtPrice}
                  label={<span>Compare Price</span>}
                  onChange={newChecked => {
                    handleCheckboxChange(
                      newChecked,
                      'isShowCompareAtPrice',
                    );
                  }}
                />
              </div>
              <div className={Styles["choise-list"]}>
                <Checkbox
                  checked={isShowSKU}
                  label={<span>SKU</span>}
                  onChange={newChecked => {
                    handleCheckboxChange(
                      newChecked,
                      'isShowSKU',
                    );
                  }}
                />
              </div>
            </div>
          </div>



        </div>

      </div>
      }
      <div
        className={`${Styles.wrapper} ${props.isCsvUploadJobRunning || props.isCsvExportJobRunning
          ? Styles.wrapperWithCsvBanner
          : ''
          } ${isLoading ||
            searchApiCalled ||
            nextPageApiCalled ||
            pageLimitApiCalled ||
            statusApiCalled
            ? Styles.wrapperLoading
            : ''
          }`}
      >
        <div className={`ag-theme-alpine ${Styles['grid-container']}`} style={{ height: '100%' }}>
          {(isLoading || searchApiCalled || nextPageApiCalled || pageLimitApiCalled || statusApiCalled) &&
            <div
              className={
                isLoading
                  ? `products-page-loading ${Styles['spinner-wrapper']}`
                  : Styles['spinner-wrapper']
              }
            >
              <Spinner accessibilityLabel="Spinner example" size="large" color="teal" />
            </div>
          }
          {/* Market Error Banner */}
          {(() => {
            const marketsWithErrors = getMarketsWithErrors();
            if (marketsWithErrors.length === 0) return null;

            if (marketsWithErrors.length === 1) {
              const market = marketsWithErrors[0];
              return (
                <Banner status="critical">
                  <DisplayText size="small">
                    <strong>{market.regionName} ({market.setCurrencyCode}):</strong> {market.marketOKMsg}
                    {" "}
                    <span className="resolveNowBtn">
                      <Button size="slim" primary onClick={() => openMarketSolutionModal(market)}>
                        View Solution
                      </Button>
                    </span>
                  </DisplayText>
                </Banner>
              );
            }
            return (
              <Banner status="critical">
                {!showAllMarketErrors ? (
                  <>
                    <DisplayText size="small">
                      Multiple markets have issues
                      {" "}
                      <span className="resolveNowBtn">
                        <Button primary onClick={() => setShowAllMarketErrors(true)}>
                          See All
                        </Button>
                      </span>
                    </DisplayText>

                  </>
                ) : (
                  <div>
                    <DisplayText size="small">
                      <div style={{ marginBottom: '10px' }}>
                        <strong>Markets with Issues:</strong>
                        {" "}
                        <Button plain onClick={() => setShowAllMarketErrors(false)}>
                          Hide
                        </Button>
                      </div>
                    </DisplayText>

                    {marketsWithErrors.map((market, index) => (
                      <div key={market.id} style={{ marginBottom: '10px', paddingLeft: '10px', borderLeft: '3px solid #bf0711' }}>
                        <div style={{ marginBottom: '5px' }}>
                          <strong>{market.regionName} ({market.setCurrencyCode}):</strong> {market.marketOKMsg}
                          <span className="resolveNowBtn">
                            <Button size="slim" primary onClick={() => openMarketSolutionModal(market)}>
                              View Solution
                            </Button>
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Banner>
            );
          })()}
          {errorMessageForPrice && (<Banner
            status="critical"
          >
            <DisplayText size="small">{errorMessageForPrice}
              {" "}{errorMessageForPrice.includes("Error! Primary market price can't be null") && <span>Mail us: <span className="emailLink">support@mlveda.com</span></span>}
              {" "}{errorMessageForPrice.includes("Some products are malfunctioned!") && <span><span className="resolveNowBtn"><Button primary onClick={() => { window.open("https://youtu.be/B0mp5sgXbyY", "_blank") }}>Resolve now</Button></span><Link onClick={() => { handleStatusChange(["all"]) }}> I'll do it later</Link></span>}
              {/* <Link>Solve it</Link> */}
            </DisplayText>
          </Banner>)}
          {(!isLoading && !searchApiCalled && !nextPageApiCalled && !pageLimitApiCalled && !statusApiCalled && hasInitialDataLoaded) && <AgGridReact
            onSelectionChanged={(event) => {
              const selectedNodes =
                event.api.getSelectedNodes();
              const totalLeafRows =
                event.api
                  .getModel()
                  .rowsToDisplay
                  .filter(row => !row.group)
                  .length;
              const selectedLeafRows =
                selectedNodes.filter(
                  node => !node.group
                ).length;
              setAllSelected(
                selectedLeafRows === totalLeafRows
              );
              if (
                selectedLeafRows === totalLeafRows ||
                selectedLeafRows === 0
              ) {
                event.api.refreshCells({
                  force: true
                });
              }
              const productIds = [
                ...new Set(
                  selectedNodes
                    .filter(node => node.data)
                    .map(node => node.data.productId)
                )
              ];
              setSelectedProductIds(productIds);
            }}
            rowData={rowData}
            rowSelection="multiple"
            groupRowRendererFramework={GroupRowRenderer}
            groupSelectsChildren={true}
            enableRangeSelection={true}
            enableFillHandle={true}
            fillHandleDirection="y"
            // enableCellTextSelection={true}
            enterMovesDownAfterEdit={true}
            suppressRowClickSelection={true}
            // frameworkComponents={{'numericCellEditor': NumericCellEditor}}
            suppressContextMenu={true}
            pagination={false}
            paginationPageSize={10}
            suppressPaginationPanel={true}
            groupMultiAutoColumn={true}
            groupDefaultExpanded={-1}
            rowGroupPanelShow={"never"}
            groupUseEntireRow={true}
            onGridReady={onGridReady}
            onColumnResized={onColumnResized}
            onCellValueChanged={params => handleDataChanged(params)}
            frameworkComponents={{ genderCellRenderer: MyHeaderGroupComponent }}
            getRowStyle={params => getRowStyle(params)}
            enableBrowserTooltips={true}
            tooltipShowDelay={0}
            suppressMovableColumns={true}
            overlayNoRowsTemplate='<span style="font-size: 1.6rem;">No products found!</span>'
            defaultColDef={{
              flex: 1,
              minWidth: 110,
              editable: true,
              resizable: true
            }}
          >
            <AgGridColumn
              field="productId"
              headerName="Title"
              rowGroup={true}
              hide={true}
              valueFormatter={(p) => rowGroupHeaderName(rowData, p)}
            />

            <AgGridColumn
              field="variantTitle"
              headerCheckboxSelection={false}
              tooltipField="variantTitle"
              editable={false}
              headerName="Product"
              pinned="left"
              cellStyle={{
                "background-color": "#f7f7f7",
                borderRight: "1px solid #D9DCE0"
              }}
              cellRendererFramework={productTitleComponent}
              headerComponentFramework={SelectAllHeader}
              headerComponentParams={{
                allSelected,
              }}
            />
            {
              storeMarkets.map((storeMarket) => {
                const hasMarketError = storeMarket.isMarketOK === false;
                const shouldShowFixButton = hasMarketError;

                return (
                  <AgGridColumn
                    key={storeMarket.id}
                    field={storeMarket.regionCode}
                    headerName={`${storeMarket.regionName}_${storeMarket.setCurrencyCode}`}
                    headerGroupComponent="genderCellRenderer"
                    headerGroupComponentParams={{
                      clicked: handleHeadClick,
                      header: "UnitedStates",
                      showFixButton: shouldShowFixButton,
                      marketData: storeMarket,
                      onFixClick: handleFixMarket,
                      hasMarketError: hasMarketError
                    }}
                  >
                    <AgGridColumn
                      field={`${storeMarket.regionCode}_invalid`}
                      headerName="Price"
                      hide={true}
                    // onCellValueChanged={params => handleDataChanged(params)}
                    />
                    <AgGridColumn
                      field={`${storeMarket.regionCode}_price`}
                      headerName="Price"
                      cellStyle={validateCell}
                      valueFormatter={param =>
                        numberValueFormatter(param, "europeSymbol")
                      }
                      // cellClass={(params) => handleCellClass(params)}
                      editable={(params) => checkEditableFlagWithMarketError(params)}
                      valueParser={param => numberParser(param, storeRoundings)}
                      minWidth="100"
                      cellClassRules={{
                        'disabled': params => {
                          if (params && params.colDef) {
                            const regionCode = params.colDef.field.split('_')[0];
                            const colId = `${regionCode}_invalid_for_hundred`;
                            if (regionCode === params.data.primayMarket) {
                              return false;
                            }
                            if (!params.data.isEditable || params.data[colId]) {
                              return true;
                            } else {
                              return false
                            }
                          } else {
                            return false;
                          }
                        }
                      }}
                      tooltipField={`${storeMarket.regionCode}_invalid_for_hundred_tooltip`}
                    // onCellValueChanged={params => handleDataChanged(params)}
                    />
                    <AgGridColumn
                      field={`${storeMarket.regionCode}_compare_at_price`}
                      headerName="Compare Price"
                      cellStyle={(params) => {
                        // Check if market has errors
                        const regionCode = params.colDef.field.split('_')[0];
                        const market = storeMarkets.find(m => m.regionCode === regionCode);
                        const hasMarketError = market && market.isMarketOK === false;

                        if (hasMarketError) {
                          return {
                            backgroundColor: "#fef3f2",
                            borderColor: "#bf0711",
                            borderRight: "1px solid #bf0711",
                            color: "#6d0000"
                          };
                        }
                        return PRICE_CELL_STYLE;
                      }}
                      valueFormatter={param =>
                        numberValueFormatter(param, "europeSymbol")
                      }
                      minWidth="115"
                      // cellClass={(params) => handleCellClass(params)}
                      editable={(params) => checkEditableFlagWithMarketError(params)}
                      valueParser={param => numberParser(param, storeRoundings)}
                      hide={!isShowCompareAtPrice}
                      cellClassRules={{
                        'disabled': params => {
                          if (params && params.colDef) {
                            const regionCode = params.colDef.field.split('_')[0];
                            const colId = `${regionCode}_invalid_for_hundred`;
                            if (regionCode === params.data.primayMarket) {
                              return false;
                            }
                            if (!params.data.isEditable || params.data[colId]) {
                              return true;
                            } else {
                              return false
                            }
                          } else {
                            return false;
                          }
                        }
                      }}
                      tooltipField={`${storeMarket.regionCode}_invalid_for_hundred_tooltip`}
                    />
                    <AgGridColumn
                      field={`${storeMarket.regionCode}_sku`}
                      headerName="SKU"
                      cellStyle={(params) => {
                        // Check if market has errors
                        const regionCode = params.colDef.field.split('_')[0];
                        const market = storeMarkets.find(m => m.regionCode === regionCode);
                        const hasMarketError = market && market.isMarketOK === false;

                        if (hasMarketError) {
                          return {
                            backgroundColor: "#fef3f2",
                            borderColor: "#bf0711",
                            borderRight: "1px solid #bf0711",
                            color: "#6d0000"
                          };
                        }
                        return PRICE_CELL_STYLE;
                      }}
                      // cellClass={(params) => handleCellClass(params)}
                      editable={(params) => checkEditableFlagWithMarketError(params)}
                      minWidth="150"
                      hide={!isShowSKU}
                      cellClassRules={{
                        'disabled': params => {
                          if (params && params.colDef) {
                            const regionCode = params.colDef.field.split('_')[0];
                            const colId = `${regionCode}_invalid_for_hundred`;
                            if (regionCode === params.data.primayMarket) {
                              return false;
                            }
                            if (!params.data.isEditable || params.data[colId]) {
                              return true;
                            } else {
                              return false
                            }
                          } else {
                            return false;
                          }
                        }
                      }}
                      tooltipField={`${storeMarket.regionCode}_invalid_for_hundred_tooltip`}
                    />
                  </AgGridColumn>
                );
              })
            }
          </AgGridReact>
          }

        </div>
      </div>
      {openFrozeAdminPanel && <FrozeAdminPanel timeInMs={timer} closeFreezeAdminPanel={closeFreezeAdminPanel} storeId={props.storeId} />}
      {isShowToast && <Frame>
        <Toast
          duration={2000}
          content={toastMessage || "Products have been updated successfully."}
          onDismiss={() => {
            setIsShowToast(false);
            setToastMessage("");
          }} />
      </Frame>}
      {(isShowErrorToast || verifyRoundingError) && <Frame>
        <Toast
          duration={errorToastDuration}
          error
          content={errorMessage || "Something went wrong."}
          onDismiss={() => {
            setIsShowErrorToast(false);
            setErrorToastDuration(ERROR_TOAST_DURATION);
          }} />
      </Frame>}
      {/* {roundingDisabledToastMarkup} */}
      <Modal
        open={isConfirmationModalOpen}
        onClose={closeConfirmationModal}
        title={timer > 2000 ? `Your application will freeze for ${timer / 1000} seconds` : `Do you want to refresh products?`}
        primaryAction={{
          content: 'Yes',
          loading: refreshProductsApiCalled,
          disabled: refreshProductsApiCalled,
          onAction: () => { handleRefreshProducts() },
        }}
        secondaryActions={[
          {
            content: 'No',
            onAction: closeConfirmationModal,
          },
        ]}
      >
        <Modal.Section>
          <p>Are you sure want to refresh products?</p>
          <p style={{ color: "grey", marginTop: '1rem' }}>If you encounter any issues with how products are appearing, selecting this option will help reload and refresh the product data, ensuring everything displays correctly.</p>
        </Modal.Section>
      </Modal>
      <Modal
        open={isConfirmModal}
        onClose={closeModal}
        primaryAction={{
          content: 'Leave',
          onAction: leavePage,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: closeModal,
          },
        ]}
      >
        <Modal.Section>
          <TextContainer>
            <p>
              Please save your changes and go to next page else your current changes will be discarded.
              <br />
              Are you sure you want to leave page?
            </p>
          </TextContainer>
        </Modal.Section>
      </Modal>
      <Modal
        open={isSelectionPaginationModal}
        onClose={closeSelectionPaginationModal}
        title="Change page?"
        primaryAction={{
          content: 'Continue',
          onAction: confirmSelectionPagination,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: closeSelectionPaginationModal,
          },
        ]}
      >
        <Modal.Section>
          <TextContainer>
            <p>
              You have selected products on this page. Changing the page will deselect them.
              Do you want to continue?
            </p>
          </TextContainer>
        </Modal.Section>
      </Modal>
      <Modal
        open={isZeroPrimaryPriceModalOpen}
        onClose={closeZeroPrimaryPriceModal}
        title="Primary market price is zero"
        primaryAction={
          canContinueRemovePrices
            ? {
              content: 'Continue',
              onAction: confirmContinueWithoutZeroPriceProducts,
            }
            : {
              content: 'Cancel',
              onAction: closeZeroPrimaryPriceModal,
            }
        }
        secondaryActions={
          canContinueRemovePrices
            ? [
              {
                content: 'Cancel',
                onAction: closeZeroPrimaryPriceModal,
              },
            ]
            : undefined
        }
      >
        <Modal.Section>
          <TextContainer>
            <p>
              {canContinueRemovePrices
                ? "The following selected product(s) have a zero primary market price. Update the price of this product(s), or click Continue to remove prices for the other selected products only."
                : "The selected product(s) have a zero primary market price. Please update the price of this product(s) before removing prices."}
            </p>
            <ul style={{ marginTop: '12px', paddingLeft: '20px' }}>
              {visibleZeroPrimaryPriceProducts.map((product) => (
                <li key={product.productId}>{product.productTitle}</li>
              ))}
              {hiddenZeroPrimaryPriceCount > 0 && (
                <li>+ {hiddenZeroPrimaryPriceCount} more</li>
              )}
            </ul>
          </TextContainer>
        </Modal.Section>
      </Modal>
      {!props.isShopifyPaymentsEnabled &&
        <Modal
          title="Enable Price rounding in Shopify payments"
          open={openRoundingDisabledModal}
          primaryAction={{
            content: "Verify",
            loading: verifyRoundingApiCalled,
            disabled: verifyRoundingApiCalled,
            onAction: () => roundingVerify(),
          }}
        >
          <Modal.Section>
            <TextContainer>
              <p>
                Please enable <span className="bold-font">Price rounding</span> in your Shopify payments. Our app will not work unless you enable rounding.
              </p>
              <p className="mt-15">Open this <Link onClick={() => { window.open(`https://${props.auth.myshopify_domain}/admin/settings/payments/shopify-payments`) }}>page</Link> and click Enable button in the <span className="bold-font">Price rounding</span> section as shown below. Your dashboard will be enabled within an hour after enabling it</p>
              <img className="roundingImg" src={roundingDisabled} alt="Rounding" />
              <p className="mt-15">Please write to us at <a rel="noreferrer noopener" aria-label=" (opens in a new tab)" href="mailto:support@mlveda.com" target="_blank"><span className="emailLink">support@mlveda.com</span></a> if you have any queries</p>
            </TextContainer>
          </Modal.Section>
        </Modal>
      }

      {selectedMarketError && (
        <Modal
          large
          open={isMarketErrorModalOpen}
          onClose={closeMarketSolutionModal}
          title={`Fix Market Issue: ${selectedMarketError.regionName} (${selectedMarketError.setCurrencyCode})`}
          primaryAction={{
            content: 'Mark as Resolved',
            onAction: handleMarkAsResolved,
          }}
          secondaryActions={[
            {
              content: 'Close',
              onAction: closeMarketSolutionModal,
            },
          ]}
        >
          <Modal.Section>
            <TextContainer>
              {/* Display the market error message */}
              <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#fef3f2', borderLeft: '4px solid #bf0711' }}>
                <p style={{ margin: 0, color: '#bf0711', fontWeight: '500' }}>
                  {selectedMarketError.marketOKMsg}
                </p>
              </div>

              {/* Display solution steps based on marketOKSteps */}
              {selectedMarketError.marketOKSteps && (() => {
                const storeDomain = props.auth?.myshopify_domain?.replace('.myshopify.com', '') || 'your-store';
                const scenario = getScenarioSteps(selectedMarketError.marketOKSteps, storeDomain);

                if (!scenario) return <p>No solution steps available.</p>;

                return (
                  <div>
                    <h3 style={{ marginBottom: '10px', color: '#202223' }}>{scenario.title}</h3>
                    <p style={{ marginBottom: '15px', fontStyle: 'italic', color: '#6d7175' }}>
                      {scenario.subtitle}
                    </p>

                    {/* Direct Link to Shopify Markets */}
                    <div style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#f6f6f7', borderRadius: '4px' }}>
                      <strong>Direct Link: </strong>
                      <Link onClick={() => window.open(scenario.storeLink, '_blank')}>
                        {scenario.storeLink}
                      </Link>
                    </div>

                    {/* Solution Steps */}
                    <div>
                      <strong>Follow these steps:</strong>
                      <ol style={{ marginTop: '10px', paddingLeft: '20px' }}>
                        {scenario.steps.map((step, index) => (
                          <li key={index} style={{ marginBottom: '8px', lineHeight: '1.6' }}>
                            {step}
                          </li>
                        ))}
                      </ol>
                    </div>

                    {/* Support contact for scenario 2 */}
                    {selectedMarketError.marketOKSteps === 'catalog-create' && (
                      <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#fff4e5', borderLeft: '4px solid #f49342', borderRadius: '4px' }}>
                        <p style={{ margin: 0 }}>
                          <strong>Need Help?</strong> Contact our support team at{' '}
                          <a href="mailto:support@mlveda.com" style={{ color: '#005bd3' }}>
                            support@mlveda.com
                          </a>
                        </p>
                      </div>
                    )}
                  </div>
                );
              })()}
            </TextContainer>
          </Modal.Section>
        </Modal>
      )}
    </>
  );
};


const mapStateToProps = (state) => {
  return {
    isMarketLoading: state.markets.isLoading,
    markets: state.markets.markets,
    currencies: state.currencies.currencies,
    auth: state.auth.storeDetails,
    isShopifyPaymentsEnabled: state.auth.storeDetails.isShopifyPaymentsEnabled,
    isCsvUploadJobRunning: Boolean(state.settings.isCsvUploadJobRunning),
    isCsvExportJobRunning: Boolean(state.settings.isCsvExportJobRunning),
  }
}
const mapDispatchToProps = {
  getMarkets
}

export default React.memo(connect(
  mapStateToProps,
  mapDispatchToProps
)(ProductsContainer));
